// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import { useState, useCallback } from 'react';
import { VERTICALS, DEMO_VERTICALS, VERTICAL_AWARE_ROLES } from './verticals-config';

const STORAGE_KEY = 'islandoverheaddoors_verticals';

/**
 * Hook for managing vertical selection state.
 * Reads/writes to localStorage for persistence.
 */
export function useVerticals() {
  const [selected, setSelected] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const saveVerticals = useCallback((ids) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    setSelected(ids);
  }, []);

  const isSelected = useCallback((id) => selected.includes(id), [selected]);

  const hasVerticals = selected.length > 0;

  const getSelectedVerticals = useCallback(
    () => selected.map(id => VERTICALS[id]).filter(Boolean),
    [selected]
  );

  return { selected, saveVerticals, isSelected, hasVerticals, getSelectedVerticals, VERTICALS };
}

/**
 * Build dynamic nav items for a given role and selected verticals.
 * Inserts vertical sections into the base nav at the correct position.
 */
export function buildVerticalNavItems(role, selectedVerticals) {
  if (!VERTICAL_AWARE_ROLES.includes(role)) return [];

  const items = [];
  const verticalIds = selectedVerticals || [];

  verticalIds.forEach(vId => {
    const vertical = VERTICALS[vId];
    if (!vertical) return;

    const allowedPages = vertical.navByRole[role] || [];
    if (allowedPages.length === 0) return;

    items.push({ section: vertical.name });
    allowedPages.forEach(pageKey => {
      const page = vertical.pages[pageKey];
      if (page) {
        items.push({
          path: page.path,
          icon: page.icon || vertical.icon,
          label: page.label,
        });
      }
    });
  });

  return items;
}

/**
 * Get vertical selection for demo users (default set).
 */
export function getDemoVerticals() {
  return DEMO_VERTICALS;
}
